import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert,
  TextField, Checkbox, CircularProgress, Box,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from "@mui/material";
import axios from "../../../config/axiosConfig";

// Genérico para Egresos/Compras/OTs (reemplaza carga_egreso.php / carga_compra.php /
// carga_ot.php + guarda_*.php): buscar por texto, tildar varias filas con checkbox,
// "Agregar" — mismo flujo que el modal.php del legacy, sin el diálogo de Bootstrap.
const BuscarYAgregarDialog = ({ open, onClose, titulo, buscarEndpoint, columns, campoId = "id", onAgregar }) => {
  const [query, setQuery] = useState("");
  const [resultados, setResultados] = useState([]);
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setResultados([]);
    setSeleccionados(new Set());
    setError("");
  }, [open]);

  useEffect(() => {
    if (!query || query.trim().length < 1) {
      setResultados([]);
      return;
    }
    setLoading(true);
    const timer = setTimeout(() => {
      axios.get(buscarEndpoint, { params: { q: query } })
        .then((res) => setResultados(res.data?.data || []))
        .catch(() => setResultados([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [query, buscarEndpoint]);

  const toggleFila = (id) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAgregar = async () => {
    setSaving(true);
    setError("");
    try {
      await onAgregar(Array.from(seleccionados));
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || "Error al agregar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>{titulo}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        <TextField
          size="small" fullWidth autoFocus label="Buscar"
          value={query} onChange={(e) => setQuery(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={22} /></Box>
        ) : resultados.length === 0 ? (
          <Alert severity="info">{query ? "No existen resultados." : "Escribe para buscar."}</Alert>
        ) : (
          <TableContainer sx={{ maxHeight: "45vh" }}>
            <Table size="small" stickyHeader sx={{ "& td": { py: 0.4, px: 0.75, fontSize: "0.78rem" } }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  {columns.map((c) => <TableCell key={c.key}>{c.label}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {resultados.map((r) => (
                  <TableRow key={r[campoId]} hover onClick={() => toggleFila(r[campoId])} sx={{ cursor: "pointer" }}>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={seleccionados.has(r[campoId])} />
                    </TableCell>
                    {columns.map((c) => (
                      <TableCell key={c.key}>{c.format ? c.format(r[c.key], r) : r[c.key]}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={handleAgregar} disabled={saving || seleccionados.size === 0}>
          {saving ? "Guardando..." : `Agregar (${seleccionados.size})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BuscarYAgregarDialog;
